import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { AnalysisResponse, Tab } from '../components/analysis/types';
import PriceChangesTab from '../components/analysis/PriceChangesTab';
import AverageShopTab from '../components/analysis/AverageShopTab';
import RetailerComparisonTab from '../components/analysis/RetailerComparisonTab';

export default function AnalysisPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params or use defaults
  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'price-changes'
  );
  const [data, setData] = useState<AnalysisResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [basketViewMode, setBasketViewMode] = useState<'threshold' | 'size'>(
    (searchParams.get('basketView') as 'threshold' | 'size') || 'size'
  );
  const [priceViewMode, setPriceViewMode] = useState<'increases' | 'decreases'>(
    (searchParams.get('priceView') as 'increases' | 'decreases') || 'increases'
  );

  // Function to update URL when state changes
  const updateUrlState = (updates: {
    tab?: Tab;
    basketView?: 'threshold' | 'size';
    priceView?: 'increases' | 'decreases';
  }) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (updates.tab !== undefined) {
        newParams.set('tab', updates.tab);
      }
      if (updates.basketView !== undefined) {
        newParams.set('basketView', updates.basketView);
      }
      if (updates.priceView !== undefined) {
        newParams.set('priceView', updates.priceView);
      }
      return newParams;
    });
  };

  // Wrapper functions for tab/mode setters that also update URL
  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    updateUrlState({ tab });
  };

  const handleBasketViewChange = (mode: 'threshold' | 'size') => {
    setBasketViewMode(mode);
    updateUrlState({ basketView: mode });
  };

  const handlePriceViewChange = (mode: 'increases' | 'decreases') => {
    setPriceViewMode(mode);
    updateUrlState({ priceView: mode });
  };

  useEffect(() => {
    // Sync state with URL params whenever URL changes (back/forward navigation)
    const tabFromUrl = (searchParams.get('tab') as Tab) || 'price-changes';
    const basketViewFromUrl = (searchParams.get('basketView') as 'threshold' | 'size') || 'size';
    const priceViewFromUrl = (searchParams.get('priceView') as 'increases' | 'decreases') || 'increases';

    setActiveTab(tabFromUrl);
    setBasketViewMode(basketViewFromUrl);
    setPriceViewMode(priceViewFromUrl);
  }, [searchParams]);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.get(`/api/analysis`);
        setData(response.data);
      } catch (err) {
        console.error('Failed to fetch analysis data:', err);
        setError('Failed to load analysis data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analysis data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
        <h2 className="text-lg font-bold mb-2">Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold text-ink-700">Shopping Analysis</h1>
        <p className="text-sm text-ink-500 max-w-2xl">
          Dive into the biggest price shifts, your go-to basket, and how other retailers compare over time.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => handleTabChange('price-changes')}
          className={`pill-tab ${activeTab === 'price-changes' ? 'pill-tab-active' : ''}`}
        >
          📈 Price changes
        </button>
        <button
          onClick={() => handleTabChange('average-shop')}
          className={`pill-tab ${activeTab === 'average-shop' ? 'pill-tab-active' : ''}`}
        >
          🛒 Average shop
        </button>
        <button
          onClick={() => handleTabChange('retailer-comparison')}
          className={`pill-tab ${activeTab === 'retailer-comparison' ? 'pill-tab-active' : ''}`}
        >
          🏬 Retailer comparison
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-8">
        {activeTab === 'price-changes' && data?.priceIncreases && (
          <PriceChangesTab
            priceIncreases={data.priceIncreases}
            priceViewMode={priceViewMode}
            onPriceViewChange={handlePriceViewChange}
          />
        )}
        {activeTab === 'average-shop' && data?.averageShop && (
          <AverageShopTab
            averageShop={data.averageShop}
            priceIncreases={data.priceIncreases}
            basketViewMode={basketViewMode}
            onBasketViewChange={handleBasketViewChange}
          />
        )}
        {activeTab === 'retailer-comparison' && data?.retailerComparison && (
          <RetailerComparisonTab retailerComparison={data.retailerComparison} />
        )}
      </div>
    </div>
  );
}

